import { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import AppUtils from '@control-front-end/utils/utils';
import { SEARCH_SMART_CHIP_REGEXP } from '@control-front-end/common/constants/regExp';
import { Utils, Cursor } from 'mw-style-react';

/**
 * Hook for managing smart chip functionality in text editors
 * @param {Object} config - Configuration object
 * @param {Object} config.editorRef - Reference to the editor element
 * @param {Function} config.onAddSmartChip - Callback when a smart chip is added
 * @param {Function} config.onRemoveSearchField - Callback when a search field is removed
 * @param {Function} config.onAddSearchField - Callback when a search field is added
 * @returns {Object} - Smart chip state and handlers
 */
function useSmartChip({
  editorRef,
  onAddSmartChip,
  onRemoveSearchField,
  onAddSearchField,
  useElementId = false,
}) {
  const [smartChipPopup, setSmartChipPopup] = useState(false);
  const [query, setQuery] = useState('');
  const smartChipPopupRef = useRef(smartChipPopup);
  const cursorPositionRef = useRef(null);

  useEffect(() => {
    smartChipPopupRef.current = smartChipPopup;
  }, [smartChipPopup]);

  // Helper function to restore cursor to stored position
  const restoreCursorPosition = () => {
    if (!editorRef.current || cursorPositionRef.current === null) return;
    Cursor.moveCursorToPosition(editorRef.current, cursorPositionRef.current);
  };

  // Handle removing a chip field
  const handleRemoveChipField = () => {
    if (editorRef.current) {
      const descrHtml = editorRef.current.innerHTML;
      const newDescr = descrHtml.replace(SEARCH_SMART_CHIP_REGEXP, '@$1');

      // Call the callback to update the text content
      onRemoveSearchField?.(newDescr);

      // Restore cursor position after the content has been updated
      setTimeout(() => {
        restoreCursorPosition();
      }, 0);
    }

    setSmartChipPopup(false);
  };

  // Handle adding a chip field
  const handleAddChipField = () => {
    // Store cursor position information for later restoration
    const currentCursorPosition = Cursor.getCaretPosition(editorRef.current);
    cursorPositionRef.current =
      currentCursorPosition === -1 ? null : currentCursorPosition;

    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    setSmartChipPopup(true);
    setQuery('');
    AppUtils.addChipField(selection, range);
    const descrHtml = editorRef.current.innerHTML;

    onAddSearchField?.(descrHtml);
  };

  // Handle adding a smart chip from popup selection
  const handleAddSmartChip = ({ objType, id, title }) => {
    const type = objType === 'graphFolder' ? 'graph' : objType;

    let bbCode = `[${type}=${id}]${Utils.stripHtml(title)}[/${type}]`;

    if (useElementId) {
      const elementId = AppUtils.createRid();
      bbCode = `[${type}=${id} elementId=${elementId}]${Utils.stripHtml(
        title
      )}[/${type}]`;

      setTimeout(() => {
        AppUtils.scrollToElementEndSetCursor(elementId);
      }, 100);
    }

    if (editorRef.current) {
      const descrHtml = editorRef.current.innerHTML;
      const newDescr = descrHtml.replace(SEARCH_SMART_CHIP_REGEXP, bbCode);

      setSmartChipPopup(false);
      onAddSmartChip?.(newDescr);
    }
  };

  // Handle key events
  const handleKeyEvent = (e) => {
    if (e.key === 'Escape') {
      handleRemoveChipField();
    } else if (e.key === 'Backspace') {
      setTimeout(() => {
        const hasSearchChip =
          editorRef.current?.querySelector('div.searchChip');
        if (!hasSearchChip && smartChipPopupRef.current) {
          handleRemoveChipField();
        }
      }, 0);
    } else if (e.key === '@' && !smartChipPopupRef.current) {
      setTimeout(handleAddChipField, 0);
    }
  };

  // Update query when smart chip content changes
  const updateQuery = () => {
    const el = editorRef.current?.querySelector('div.searchChip');
    setQuery(el ? el.innerText : '');
    if (!el && smartChipPopupRef.current) {
      setSmartChipPopup(false);
    }
  };

  return {
    smartChipPopup,
    setSmartChipPopup,
    query,
    setQuery,
    updateQuery,
    handleKeyEvent,
    handleRemoveChipField,
    handleAddChipField,
    handleAddSmartChip,
  };
}

useSmartChip.propTypes = {
  editorRef: PropTypes.object.isRequired,
  onAddSmartChip: PropTypes.func,
  onRemoveSearchField: PropTypes.func,
  onAddSearchField: PropTypes.func,
  useElementId: PropTypes.bool,
};

export default useSmartChip;
